import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageComponent } from '../page/page.component';
import {
  OrderDetailDismissPayload,
  OrderDetailViewComponent,
} from '../order-detail-view/order-detail-view.component';
import { OrderService } from '../../services/order.service';
import { TenantSelectionService } from '../../services/tenant-selection.service';
import { extractErrorMessage } from '../../utils/error.utils';
import { Order } from '@crowdctrl/core';
import { CrowdctrlToastService } from '../../services/crowdctrl-toast.service';
import { Observable, defer, firstValueFrom, from } from 'rxjs';

@Component({
  selector: 'crowdctrl-order-placed',
  standalone: true,
  imports: [CommonModule, PageComponent, OrderDetailViewComponent],
  templateUrl: './order-placed.component.html',
  styleUrl: './order-placed.component.css',
})
export class OrderPlacedComponent implements OnChanges {
  @Input() workspaceId: string | null = null;
  @Input() sessionId: string | null = null;
  @Input() orderId: string | null = null;
  /** When set (e.g. public order-follow page), skips authenticated order fetch. */
  @Input() publicOrder: Order | null = null;
  @Input() showHeader = true;
  @Input() defaultHref: string | null = null;
  /** When true, content flows for page-level scrolling (e.g. agent app) */
  @Input() flowLayout = false;
  /** When true, only show main ticket number (agent app) */
  @Input() compact = false;
  /** Passed through to order detail view; see OrderDetailViewComponent.hideExtendedOrderDetails */
  @Input() hideExtendedOrderDetails = false;

  @Output() dismiss = new EventEmitter<OrderDetailDismissPayload>();

  @ViewChild(PageComponent) private page?: PageComponent;
  @ViewChild(OrderDetailViewComponent) private orderDetailView?: OrderDetailViewComponent;

  order: Order | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  readonly pageLoadFn = (): Observable<unknown> => defer(() => from(this.loadOrderIfReady()));
  readonly pageRefreshFn = (): Observable<unknown> => defer(() => from(this.loadOrderIfReady()));

  private orderService = inject(OrderService);
  private tenantSelectionService = inject(TenantSelectionService);
  private toastService = inject(CrowdctrlToastService);

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['publicOrder'] ||
      changes['orderId'] ||
      changes['workspaceId'] ||
      changes['sessionId']
    ) {
      void this.loadOrderIfReady();
    }
  }

  private applyPublicOrder(order: Order): void {
    this.order = order;
    this.isLoading = false;
    this.errorMessage = null;
  }

  private async loadOrderIfReady(): Promise<void> {
    if (this.publicOrder) {
      this.applyPublicOrder(this.publicOrder);
      return;
    }

    if (!this.orderId || !this.workspaceId || !this.sessionId) {
      this.isLoading = false;
      this.errorMessage = this.orderId ? null : 'Order ID is missing.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.order = null;

    try {
      const tenantId = await this.tenantSelectionService.getEffectiveTenantId();
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      const result = await firstValueFrom(
        this.orderService.getOrder(this.workspaceId, this.sessionId, this.orderId),
      );

      if (result && result.order) {
        this.order = result.order;
      } else {
        this.errorMessage = 'Order not found.';
      }
    } catch (error) {
      console.error('Error loading order:', error);
      const errorMsg = extractErrorMessage(error) || 'Failed to load order.';
      this.errorMessage = errorMsg;

      await this.toastService.show({
        message: errorMsg,
        duration: 3000,
        color: 'danger',
      });
    } finally {
      this.isLoading = false;
    }
  }

  onDismiss(payload: OrderDetailDismissPayload): void {
    this.dismiss.emit(payload);
  }

  refreshOrder(): void {
    this.page?.onRefresh();
  }

  get isPageRefreshDisabled(): boolean {
    return !!(this.page?.isLoadInFlight || this.page?.isRefreshCoolingDown);
  }

  resetDismissNavigationState(): void {
    this.orderDetailView?.resetDismissNavigationState();
  }
}
